// src/pages/Seller/order.jsx
// ✅ Full supply-chain view: payment proofs, shipment tracking, admin PO notes
import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Box, Button, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, TextField, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Avatar, Tooltip, LinearProgress,
  FormControl, InputLabel, Select, MenuItem, Alert, Divider, Tabs, Tab,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
  Search, Refresh, Visibility, ShoppingCart, CheckCircle,
  Schedule, Cancel, LocalShipping, Done, Payment, ExpandMore,
  Description, OpenInNew, Assignment, InfoOutlined,
} from '@mui/icons-material';
import { sellerAPI } from '../../services/api';
import toast from 'react-hot-toast';

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' }); }
  catch { return '—'; }
};
const fmt = (v) => new Intl.NumberFormat('en-US', { style:'currency', currency:'USD' }).format(v || 0);

const STATUS_COLOR = {
  pending:'warning', confirmed:'info', processing:'info',
  invoice_sent:'secondary', shipped:'primary', delivered:'success', cancelled:'error',
};
const STATUS_ICON = {
  pending:      <Schedule fontSize="small" />,
  confirmed:    <CheckCircle fontSize="small" />,
  processing:   <CheckCircle fontSize="small" />,
  invoice_sent: <CheckCircle fontSize="small" />,
  shipped:      <LocalShipping fontSize="small" />,
  delivered:    <Done fontSize="small" />,
  cancelled:    <Cancel fontSize="small" />,
};
const VALID_TRANSITIONS = {
  pending:   ['confirmed', 'cancelled'],
  confirmed: ['processing', 'cancelled'],
  processing:['shipped'],
  shipped:   ['delivered'],
  delivered: [], cancelled: [],
};
const TAB_STATUSES = ['all','pending','confirmed','processing','invoice_sent','shipped','delivered','cancelled'];

// ── Payment Proof Viewer ──────────────────────────────────────────────────────
const ProofViewer = ({ url, label }) => {
  if (!url) return null;
  const isImg = /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
  const isPdf = /\.pdf$/i.test(url);
  return (
    <Box sx={{ border:'1px solid', borderColor:'divider', borderRadius:1, overflow:'hidden', mb:1 }}>
      {isImg && (
        <Box component="img" src={url} alt={label}
          sx={{ width:'100%', maxHeight:300, objectFit:'contain', display:'block' }} />
      )}
      {isPdf && (
        <iframe src={url} title={label} width="100%" height="280px"
          style={{ border:'none', display:'block' }} />
      )}
      {!isImg && !isPdf && (
        <Box sx={{ p:2 }}>
          <Typography variant="body2" color="text.secondary">
            File attached (unknown type)
          </Typography>
        </Box>
      )}
      <Box sx={{ p:1, display:'flex', gap:1, bgcolor:'grey.50' }}>
        <Button size="small" startIcon={<OpenInNew />}
          onClick={() => window.open(url, '_blank')}>
          Open in New Tab
        </Button>
      </Box>
    </Box>
  );
};

// ── Shipment Tracker ─────────────────────────────────────────────────────────
const ShipmentTracker = ({ shipment }) => {
  if (!shipment) return (
    <Alert severity="info" icon={<LocalShipping />}>
      No shipment assigned to this order yet. Admin will create it once payment is confirmed.
    </Alert>
  );
  const steps = ['pending','shipped','in_transit','arrived','delivered'];
  const idx = steps.indexOf(shipment.status);
  return (
    <Box>
      {/* Status chips row */}
      <Box sx={{ display:'flex', gap:1, flexWrap:'wrap', mb:2 }}>
        {steps.map((s, i) => (
          <Chip key={s} label={s.replace('_',' ')}
            color={i <= idx ? 'primary' : 'default'}
            variant={i === idx ? 'filled' : 'outlined'}
            size="small" sx={{ textTransform:'capitalize' }} />
        ))}
      </Box>
      <Grid container spacing={1.5}>
        {[
          ['Container',    shipment.container_number],
          ['BL Number',    shipment.bl_number],
          ['Vessel',       shipment.vessel_name],
          ['Shipping Line',shipment.shipping_line],
          ['Container Type',shipment.container_type],
          ['Temp. Control',shipment.temperature_control],
          ['Transit Days', shipment.transit_days ? `${shipment.transit_days} days` : null],
          ['Loading Port', shipment.port_of_loading],
          ['Discharge Port',shipment.port_of_discharge],
          ['Departure',    fmtDate(shipment.expected_departure)],
          ['ETA',          fmtDate(shipment.expected_arrival)],
          ['Notes',        shipment.notes],
        ].filter(([,v]) => v).map(([label, value]) => (
          <Grid item xs={6} key={label}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="body2" fontWeight="medium">{value}</Typography>
          </Grid>
        ))}
      </Grid>
      {shipment.tracking_url && (
        <Button variant="outlined" size="small" startIcon={<OpenInNew />} sx={{ mt:1.5 }}
          onClick={() => window.open(shipment.tracking_url, '_blank')}>
          Track Shipment Online
        </Button>
      )}
    </Box>
  );
};

const SellerOrders = () => {
  const [enquiries,    setEnquiries]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [searchTerm,   setSearchTerm]   = useState('');
  const [tabValue,     setTabValue]     = useState(0);
  const [selected,     setSelected]     = useState(null);
  const [viewDialog,   setViewDialog]   = useState(false);
  const [statusDialog, setStatusDialog] = useState(null);
  const [newStatus,    setNewStatus]    = useState('');
  const [updating,     setUpdating]     = useState(false);

  useEffect(() => { fetchOrders(); }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      // Use enhanced /seller/orders endpoint (includes payments + shipment)
      const res  = await sellerAPI.getMyOrders();
      const data = res.data;
      setEnquiries(Array.isArray(data) ? data : (data.orders || data.enquiries || []));
    } catch {
      // Fallback to basic enquiries endpoint
      try {
        const res2 = await sellerAPI.getMyEnquiries();
        const d2 = res2.data;
        setEnquiries(Array.isArray(d2) ? d2 : (d2.enquiries || []));
      } catch {
        toast.error('Failed to load orders');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!newStatus || !statusDialog) return;
    setUpdating(true);
    try {
      await sellerAPI.updateEnquiryStatus(statusDialog.id, { status: newStatus });
      toast.success(`Order status updated to ${newStatus}`);
      setStatusDialog(null);
      setNewStatus('');
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const counts = TAB_STATUSES.reduce((acc, s) => {
    acc[s] = s === 'all' ? enquiries.length : enquiries.filter(e => e.status === s).length;
    return acc;
  }, {});

  const statusFilter = TAB_STATUSES[tabValue];
  const filtered = enquiries.filter(e => {
    const q = searchTerm.toLowerCase();
    const matchSearch = !q || String(e.id).includes(q) ||
      (e.product || e.productName || '').toLowerCase().includes(q) ||
      (e.buyerName || '').toLowerCase().includes(q);
    const matchStatus = statusFilter === 'all' || e.status === statusFilter;
    return matchSearch && matchStatus;
  });

  if (loading) return (
    <Container sx={{ py:4 }}>
      <LinearProgress sx={{ mb:2 }} />
      <Typography color="text.secondary">Loading orders...</Typography>
    </Container>
  );

  return (
    <Container maxWidth="xl" sx={{ py:3 }}>

      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Orders & Enquiries</Typography>
          <Typography color="text.secondary">
            Full order view — payment proofs, shipment tracking and admin instructions included
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchOrders}>Refresh</Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb:4 }}>
        {[
          { label:'Total',     value: counts.all,       color:'#2196f3' },
          { label:'Pending',   value: counts.pending,   color:'#ff9800' },
          { label:'Confirmed', value: counts.confirmed, color:'#00bcd4' },
          { label:'Shipped',   value: counts.shipped,   color:'#9c27b0' },
          { label:'Delivered', value: counts.delivered, color:'#4caf50' },
          { label:'Cancelled', value: counts.cancelled, color:'#f44336' },
        ].map((s, i) => (
          <Grid item xs={6} sm={4} md={2} key={i}>
            <Card sx={{ borderRadius:2 }}>
              <CardContent sx={{ p:2, '&:last-child':{pb:2} }}>
                <Typography variant="body2" color="textSecondary">{s.label}</Typography>
                <Typography variant="h4" fontWeight="bold" color={s.color}>{s.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb:3, borderRadius:2 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable">
          {TAB_STATUSES.map((s) => (
            <Tab key={s} label={`${s.charAt(0).toUpperCase()+s.slice(1)} (${counts[s]})`} />
          ))}
        </Tabs>
      </Paper>

      {/* Search */}
      <Paper sx={{ p:2, mb:3, borderRadius:2 }}>
        <TextField fullWidth size="small"
          placeholder="Search by order ID, product, or buyer..."
          value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          InputProps={{ startAdornment:<InputAdornment position="start"><Search /></InputAdornment> }}
        />
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius:2, overflow:'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor:'grey.50' }}>
              <TableRow>
                {['Order ID','Product','Buyer','Qty','Amount','Payment','Shipment','Status','Actions'].map(h => (
                  <TableCell key={h}><Typography variant="subtitle2" fontWeight="bold">{h}</Typography></TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py:8 }}>
                    <ShoppingCart sx={{ fontSize:64, color:'text.disabled', display:'block', mx:'auto', mb:2 }} />
                    <Typography variant="h6" color="text.secondary">No orders found</Typography>
                  </TableCell>
                </TableRow>
              ) : filtered.map(e => {
                const hasProof   = (e.payments || []).some(p => p.proof_url);
                const hasShip    = !!e.shipment;
                const hasPoNote  = !!e.seller_notes;
                return (
                  <TableRow key={e.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">#{e.id}</Typography>
                      {hasPoNote && (
                        <Chip label="PO Note" size="small" color="warning" variant="outlined"
                          sx={{ fontSize:'0.6rem', height:16, mt:0.5 }} />
                      )}
                    </TableCell>
                    <TableCell>{e.product || e.productName || `Fruit #${e.fruit_id}`}</TableCell>
                    <TableCell>{e.buyerName || '—'}</TableCell>
                    <TableCell>{e.quantity || '—'}</TableCell>
                    <TableCell>{e.total_amount ? fmt(e.total_amount) : '—'}</TableCell>
                    <TableCell>
                      {hasProof
                        ? <Chip label="Proof Attached" size="small" color="success" icon={<Payment />} />
                        : <Chip label="No Proof Yet" size="small" color="default" variant="outlined" />
                      }
                    </TableCell>
                    <TableCell>
                      {hasShip
                        ? <Chip label={e.shipment.status} size="small" color="primary"
                            icon={<LocalShipping />} sx={{ textTransform:'capitalize' }} />
                        : <Chip label="Not Shipped" size="small" color="default" variant="outlined" />
                      }
                    </TableCell>
                    <TableCell>
                      <Chip label={e.status} size="small"
                        color={STATUS_COLOR[e.status] || 'default'}
                        icon={STATUS_ICON[e.status]}
                        sx={{ textTransform:'capitalize' }} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display:'flex', gap:0.5 }}>
                        <Tooltip title="View Full Details">
                          <IconButton size="small" color="primary"
                            onClick={() => { setSelected(e); setViewDialog(true); }}>
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        {VALID_TRANSITIONS[e.status]?.length > 0 && (
                          <Tooltip title="Update Status">
                            <IconButton size="small" color="success"
                              onClick={() => { setStatusDialog(e); setNewStatus(''); }}>
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ p:2, borderTop:1, borderColor:'divider' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filtered.length} of {enquiries.length} orders
          </Typography>
        </Box>
      </Paper>

      {/* ── Full Order Detail Dialog ─────────────────────────────────────────── */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="md" fullWidth>
        {selected && (
          <>
            <DialogTitle>
              <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <Typography variant="h6">Order #{selected.id} — Full Details</Typography>
                <Chip label={selected.status} color={STATUS_COLOR[selected.status] || 'default'}
                  sx={{ textTransform:'capitalize' }} />
              </Box>
            </DialogTitle>
            <DialogContent>

              {/* ① Admin Purchase Order Note */}
              {selected.seller_notes && (
                <Alert severity="warning" icon={<Assignment />} sx={{ mb:2 }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    📋 Admin Purchase Order Instructions:
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace:'pre-line' }}>
                    {selected.seller_notes}
                  </Typography>
                </Alert>
              )}

              {/* ② Basic order info */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight="bold">Order Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {[
                      ['Product',     selected.product || selected.productName || `Fruit #${selected.fruit_id}`],
                      ['Quantity',    selected.quantity ? `${selected.quantity} containers` : '—'],
                      ['Total Amount',selected.total_amount ? fmt(selected.total_amount) : '—'],
                      ['Unit Price',  selected.unit_price  ? fmt(selected.unit_price)  : '—'],
                      ['Buyer',       selected.buyerName   || '—'],
                      ['Buyer Email', selected.buyerEmail  || '—'],
                      ['Buyer Phone', selected.buyerPhone  || '—'],
                      ['Port of Discharge', selected.port_of_discharge || '—'],
                      ['Origin',      selected.selected_origin   || '—'],
                      ['Variety',     selected.selected_variety  || '—'],
                      ['Grade',       selected.selected_grade    || '—'],
                      ['Created',     fmtDate(selected.created_at)],
                      ['Updated',     fmtDate(selected.updated_at)],
                      ['Buyer Message', selected.message || '—'],
                    ].map(([label, value]) => (
                      <Grid item xs={12} sm={6} key={label}>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                        <Typography variant="body2" fontWeight="medium">{value}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                  {selected.adminResponse && (
                    <Alert severity="info" sx={{ mt:2 }} icon={<InfoOutlined />}>
                      <Typography variant="body2" fontWeight="bold">Admin Response:</Typography>
                      <Typography variant="body2">{selected.adminResponse}</Typography>
                    </Alert>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* ③ Payment Proofs Section */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                    <Payment color="success" />
                    <Typography fontWeight="bold">
                      Payment Proofs ({(selected.payments || []).filter(p => p.proof_url).length} attached)
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {(selected.payments || []).length === 0 ? (
                    <Alert severity="info">No payments recorded for this order yet.</Alert>
                  ) : (selected.payments || []).map((p, i) => (
                    <Box key={p.id || i} sx={{ mb:2 }}>
                      <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1 }}>
                        <Chip label={p.status} size="small"
                          color={p.status === 'verified' ? 'success' : p.status === 'pending' ? 'warning' : 'default'} />
                        <Typography variant="body2" fontWeight="medium">
                          {p.amount ? fmt(p.amount) : 'Amount unknown'} via {p.payment_method || 'Unknown method'}
                        </Typography>
                        {p.transaction_id && (
                          <Typography variant="caption" color="text.secondary">
                            TxID: {p.transaction_id}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {fmtDate(p.created_at)}
                        </Typography>
                      </Box>
                      {p.proof_url ? (
                        <ProofViewer url={p.proof_url} label={`Payment proof #${i+1}`} />
                      ) : (
                        <Alert severity="warning" sx={{ py:0.5 }}>
                          Buyer has not uploaded payment proof for this payment yet.
                        </Alert>
                      )}
                      {i < (selected.payments || []).length - 1 && <Divider sx={{ my:1 }} />}
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>

              {/* ④ Shipment Tracking Section */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                    <LocalShipping color="primary" />
                    <Typography fontWeight="bold">
                      Shipment Tracking
                      {selected.shipment && (
                        <Chip label={selected.shipment.status} size="small" color="primary"
                          sx={{ ml:1, textTransform:'capitalize' }} />
                      )}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <ShipmentTracker shipment={selected.shipment} />
                </AccordionDetails>
              </Accordion>

            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewDialog(false)}>Close</Button>
              {VALID_TRANSITIONS[selected.status]?.length > 0 && (
                <Button variant="contained" color="primary"
                  onClick={() => { setViewDialog(false); setStatusDialog(selected); setNewStatus(''); }}>
                  Update Order Status
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={Boolean(statusDialog)} onClose={() => setStatusDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Update Order Status</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb:2 }}>
            Order #{statusDialog?.id} — Current: <strong>{statusDialog?.status}</strong>
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>New Status</InputLabel>
            <Select label="New Status" value={newStatus} onChange={e => setNewStatus(e.target.value)}>
              {(VALID_TRANSITIONS[statusDialog?.status] || []).map(s => (
                <MenuItem key={s} value={s} sx={{ textTransform:'capitalize' }}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleStatusUpdate}
            disabled={!newStatus || updating}>
            {updating ? 'Updating...' : 'Confirm Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SellerOrders;
